import {announce} from '@github-ui/aria-live'
import type {OnDropArgs} from '@github-ui/drag-and-drop'
import {dynamic} from '@github-ui/dynamic'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import type {DashboardSectionConfig} from '@github-ui/shared-dashboard-lists/pulls/types/list-types'
import {Button, Dialog, Spinner} from '@primer/react'
import {Suspense, type ComponentProps, useCallback, useEffect, useMemo, useState} from 'react'

import {useUpdateSectionPreferences} from '../../hooks/use-update-section-preferences'
import styles from './CustomizeSectionsDialog.module.css'

const LazyCustomizeSectionsDialogContent = dynamic(async () => {
  const m = await import('./CustomizeSectionsDialogContent')
  return {default: m.CustomizeSectionsDialogContent}
})

export const preloadCustomizeSectionsDialogContent = LazyCustomizeSectionsDialogContent.preload

export interface CustomizeSectionsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Currently rendered section ids and order, drawn from the layout payload. */
  initialOrder: string[]
  /** Currently hidden section ids, drawn from the layout payload. */
  initialHidden: string[]
  /** Section ids enabled for the current viewer (after feature-flag filtering). */
  availableSections: DashboardSectionConfig[]
  /** Default order to restore when the user clicks "Reset to default". */
  defaultOrder: string[]
  /** Called with the saved preferences after they are written to client cookies, so the surface state context can apply them optimistically. */
  onSaved: (prefs: {order: string[]; hidden: string[]}) => void
  returnFocusRef?: ComponentProps<typeof Dialog>['returnFocusRef']
}

export function CustomizeSectionsDialog({open, ...props}: CustomizeSectionsDialogProps) {
  // Gate on `open` at the outer boundary so the inner component (and its
  // unsaved-edit `useState`) is fully unmounted on close. Reopening then
  // re-derives state from the current `initialOrder`/`initialHidden` props
  // instead of resurrecting whatever the user was editing before Cancel.
  if (!open) return null
  return <CustomizeSectionsDialogShell {...props} />
}

type CustomizeSectionsDialogShellProps = Omit<CustomizeSectionsDialogProps, 'open'>

function CustomizeSectionsDialogShell({
  onOpenChange,
  initialOrder,
  initialHidden,
  availableSections,
  defaultOrder,
  onSaved,
  returnFocusRef,
}: CustomizeSectionsDialogShellProps) {
  const sectionsById = useMemo(() => {
    const map = new Map<string, DashboardSectionConfig>()
    for (const section of availableSections) map.set(section.id, section)
    return map
  }, [availableSections])

  const [localDialogState, setLocalDialogState] = useState<DialogState>(() =>
    buildInitialState(initialOrder, initialHidden, availableSections),
  )
  const {order, hiddenSet} = localDialogState
  const {visible, hidden} = useMemo(() => {
    const visibleIds: string[] = []
    const hiddenIds: string[] = []
    for (const id of order) (hiddenSet.has(id) ? hiddenIds : visibleIds).push(id)
    return {visible: visibleIds, hidden: hiddenIds}
  }, [order, hiddenSet])
  const [isResetToDefault, setIsResetToDefault] = useState(false)
  const [isContentReady, setIsContentReady] = useState(false)

  const {saveSectionPreferences} = useUpdateSectionPreferences()

  const labelFor = useCallback((id: string) => sectionsById.get(id)?.name ?? id, [sectionsById])
  const handleContentReady = useCallback(() => setIsContentReady(true), [])

  // All edit callbacks use functional `setLocalDialogState(prev => ...)` so they don't
  // close over `localDialogState` — that keeps their identities stable across edits and
  // prevents `DragAndDrop` children from re-rendering on every state update.
  const moveVisible = useCallback(
    (id: string, direction: -1 | 1) => {
      setLocalDialogState(prev => computeMoveVisible(prev, id, direction) ?? prev)
      setIsResetToDefault(false)
      announce(`Moved ${labelFor(id)} ${direction === -1 ? 'up' : 'down'}`)
    },
    [labelFor],
  )

  const hideSection = useCallback(
    (id: string) => {
      setLocalDialogState(prev => computeHideSection(prev, id) ?? prev)
      setIsResetToDefault(false)
      announce(`Hid ${labelFor(id)}`)
    },
    [labelFor],
  )

  const showSection = useCallback(
    (id: string) => {
      setLocalDialogState(prev => computeShowSection(prev, id) ?? prev)
      setIsResetToDefault(false)
      announce(`Restored ${labelFor(id)}`)
    },
    [labelFor],
  )

  const onDrop = useCallback(
    ({dragMetadata, dropMetadata, isBefore}: OnDropArgs<string>) => {
      if (!dropMetadata || dragMetadata.id === dropMetadata.id) return
      setLocalDialogState(prev => computeReorder(prev, dragMetadata.id, dropMetadata.id, isBefore) ?? prev)
      setIsResetToDefault(false)
      announce(`Moved ${labelFor(dragMetadata.id)}`)
    },
    [labelFor],
  )

  const reset = useCallback(() => {
    const validDefault = defaultOrder.filter(id => sectionsById.has(id))
    setLocalDialogState({order: validDefault, hiddenSet: new Set()})
    setIsResetToDefault(true)
    announce('Reset to default order')
  }, [defaultOrder, sectionsById])

  const handleClose = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  const handleSave = useCallback(() => {
    if (visible.length === 0) return
    const prefs = isResetToDefault ? {order: [], hidden: []} : {order, hidden: [...hiddenSet]}
    saveSectionPreferences(prefs)
    onSaved(prefs)
    handleClose()
  }, [visible.length, order, hiddenSet, isResetToDefault, saveSectionPreferences, onSaved, handleClose])

  return (
    <Dialog
      title="Customize sections"
      onClose={handleClose}
      returnFocusRef={returnFocusRef}
      width="xlarge"
      renderFooter={() =>
        isContentReady ? (
          <Dialog.Footer className={styles.footer}>
            <Button variant="danger" onClick={reset}>
              Reset to default
            </Button>
            <div className={styles.footerSpacer} />
            <Button onClick={handleClose}>Cancel</Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </Dialog.Footer>
        ) : null
      }
    >
      <ErrorBoundary boundaryName="CustomizeSectionsDialog" fallback={<CustomizeSectionsDialogLoadError />}>
        <Suspense fallback={<CustomizeSectionsDialogLoading />}>
          <LazyCustomizeSectionsDialogContent
            sectionsById={sectionsById}
            visible={visible}
            hidden={hidden}
            labelFor={labelFor}
            onDrop={onDrop}
            onMove={moveVisible}
            onHide={hideSection}
            onShow={showSection}
          />
          <ContentReadySignal onReady={handleContentReady} />
        </Suspense>
      </ErrorBoundary>
    </Dialog>
  )
}

// Only commits once the lazy content beside it has resolved and rendered, so the
// footer stays hidden while that chunk is loading and if it fails.
function ContentReadySignal({onReady}: {onReady: () => void}) {
  useEffect(onReady, [onReady])
  return null
}

function CustomizeSectionsDialogLoading() {
  useEffect(() => {
    announce('Loading customize sections')
  }, [])

  return (
    <div className="d-flex flex-column flex-items-center gap-2 py-3">
      <Spinner size="large" />
      <span>Loading customize sections…</span>
    </div>
  )
}

function CustomizeSectionsDialogLoadError() {
  useEffect(() => {
    announce('The customize sections dialog failed to load. Reload the page to try again.', {
      assertive: true,
    })
  }, [])

  // The browser caches a failed module fetch, so reopening the dialog can never refetch the chunk.
  return (
    <div className="d-flex flex-column flex-items-start gap-2 py-3">
      <span>The customize sections dialog failed to load.</span>
      <Button onClick={() => window.location.reload()}>Reload page</Button>
    </div>
  )
}

type DialogState = {order: string[]; hiddenSet: Set<string>}

// Builds the dialog's local order/hidden state from persisted preferences,
// filtering out unavailable sections and appending newly available ones.
function buildInitialState(
  initialOrder: string[],
  initialHidden: string[],
  availableSections: DashboardSectionConfig[],
): DialogState {
  const availableIds = new Set(availableSections.map(section => section.id))
  const hiddenSet = new Set(initialHidden.filter(id => availableIds.has(id)))
  const preferredOrder = initialOrder.length > 0 ? initialOrder : [...availableIds]

  // Keep ids that exist; append any newly-available ids to the end.
  const order = preferredOrder.filter(id => availableIds.has(id))
  const orderedIds = new Set(order)
  for (const id of availableIds) {
    if (!orderedIds.has(id)) order.push(id)
  }

  return {order, hiddenSet}
}

// Moves a visible section up or down relative to the next visible neighbor,
// while preserving hidden sections in their existing order slots.
function computeMoveVisible(state: DialogState, id: string, direction: -1 | 1): DialogState | null {
  // Walk `order` from the section's index in the chosen direction until we
  // land on the next visible id, then swap them in place. Hidden ids between
  // them keep their original `order` slots.
  const idIdx = state.order.indexOf(id)
  if (idIdx < 0) return null
  let neighbourIdx = idIdx + direction
  while (neighbourIdx >= 0 && neighbourIdx < state.order.length) {
    const candidate = state.order[neighbourIdx]
    if (candidate !== undefined && !state.hiddenSet.has(candidate)) break
    neighbourIdx += direction
  }
  if (neighbourIdx < 0 || neighbourIdx >= state.order.length) return null
  const next = [...state.order]
  const a = next[idIdx]
  const b = next[neighbourIdx]
  if (a === undefined || b === undefined) return null
  next[idIdx] = b
  next[neighbourIdx] = a
  return {order: next, hiddenSet: state.hiddenSet}
}

// Marks a section hidden when doing so would still leave at least one section visible.
function computeHideSection(state: DialogState, id: string): DialogState | null {
  if (state.hiddenSet.has(id)) return null
  // At least one section must remain visible.
  if (state.order.length - state.hiddenSet.size <= 1) return null
  const nextHidden = new Set(state.hiddenSet)
  nextHidden.add(id)
  return {order: state.order, hiddenSet: nextHidden}
}

// Restores a hidden section
function computeShowSection(state: DialogState, id: string): DialogState | null {
  if (!state.hiddenSet.has(id)) return null
  const nextHidden = new Set(state.hiddenSet)
  nextHidden.delete(id)
  return {order: state.order, hiddenSet: nextHidden}
}

// Reorders a dragged section around a visible drop target while keeping hidden ids in the order array.
function computeReorder(
  state: DialogState,
  draggedId: string,
  targetId: string,
  isBefore: boolean,
): DialogState | null {
  // Drops onto a hidden anchor or an unknown id are ignored.
  if (state.hiddenSet.has(targetId) || !state.order.includes(draggedId) || !state.order.includes(targetId)) {
    return null
  }
  const next: string[] = []
  for (const item of state.order) {
    if (item === draggedId) continue
    if (item === targetId) {
      if (isBefore) next.push(draggedId, item)
      else next.push(item, draggedId)
    } else {
      next.push(item)
    }
  }
  return {order: next, hiddenSet: state.hiddenSet}
}
